/**
 * pfh-mobile route tree — react-router v8 (IOS-1, pfh-mobile#18).
 *
 * Architecture: pages are mounted at their original paths (no /pazuru/ or /tsumi/ prefix)
 * so that internal navigation within Pazuru and Tsumi pages works without modification.
 * Each section is wrapped in its required context providers.
 *
 * Pazuru entry: /accounts   Tsumi entry: /portfolio
 */
import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router'
import { RootLayout } from './layouts/RootLayout'
import { RouteError } from './components/RouteError'
import { FLAVOR } from './flavor/flavor'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { ImportPage } from './pages/ImportPage'
import { ProfilePage } from './pages/foroba/ProfilePage'
import { SettingsPage } from './pages/foroba/SettingsPage'
import { SecurityPage } from './pages/foroba/SecurityPage'
import { HouseholdPage } from './pages/foroba/HouseholdPage'
import { LegalPage } from './pages/foroba/LegalPage'

// Pazuru context providers — required by Money, Sidebar, and other components
import { CurrencyProvider } from '@pazuru/context/currency'
import { PrivacyProvider } from '@pazuru/context/privacy'

// ── Pazuru pages (bank accounts & transactions) ────────────────────────────
const AccountsPage = lazy(() =>
  import('@pazuru/pages/Accounts').then((m) => ({ default: m.AccountsPage })),
)
const AccountDetailPage = lazy(() =>
  import('@pazuru/pages/AccountDetail').then((m) => ({ default: m.AccountDetailPage })),
)
const TransactionsPage = lazy(() =>
  import('@pazuru/pages/Transactions').then((m) => ({ default: m.TransactionsPage })),
)
const RecurringPage = lazy(() =>
  import('@pazuru/pages/Recurring').then((m) => ({ default: m.RecurringPage })),
)
const SpendingPage = lazy(() =>
  import('@pazuru/pages/Spending').then((m) => ({ default: m.SpendingPage })),
)
const ForecastsPage = lazy(() =>
  import('@pazuru/pages/Forecasts').then((m) => ({ default: m.ForecastsPage })),
)
const BudgetsPage = lazy(() =>
  import('@pazuru/pages/Budgets').then((m) => ({ default: m.BudgetsPage })),
)
const GoalsPage = lazy(() =>
  import('@pazuru/pages/Goals').then((m) => ({ default: m.GoalsPage })),
)
const DebtsPage = lazy(() =>
  import('@pazuru/pages/DebtsPage').then((m) => ({ default: m.DebtsPage })),
)
const PazuruJeliLabPage = lazy(() =>
  import('@pazuru/pages/JeliLab').then((m) => ({ default: m.JeliLabPage })),
)
const PazuruImportLogPage = lazy(() =>
  import('@pazuru/pages/ImportLogPage').then((m) => ({ default: m.ImportLogPage })),
)

// ── Tsumi pages (ETF/crypto portfolio) ────────────────────────────────────
const PortfolioPage = lazy(() =>
  import('@tsumi/pages/PortfolioPage').then((m) => ({ default: m.PortfolioPage })),
)
const EtfExplorerPage = lazy(() =>
  import('@tsumi/pages/EtfExplorerPage').then((m) => ({ default: m.EtfExplorerPage })),
)
const CryptoExplorerPage = lazy(() =>
  import('@tsumi/pages/CryptoExplorerPage').then((m) => ({ default: m.CryptoExplorerPage })),
)
const AssetDetailPage = lazy(() =>
  import('@tsumi/pages/AssetDetailPage').then((m) => ({ default: m.AssetDetailPage })),
)
const MarketAnalysisPage = lazy(() =>
  import('@tsumi/pages/MarketAnalysisPage').then((m) => ({ default: m.MarketAnalysisPage })),
)
const BacktestPage = lazy(() =>
  import('@tsumi/pages/BacktestPage').then((m) => ({ default: m.BacktestPage })),
)
const ScreenerPage = lazy(() =>
  import('@tsumi/pages/ScreenerPage').then((m) => ({ default: m.ScreenerPage })),
)
const SentimentPage = lazy(() =>
  import('@tsumi/pages/SentimentPage').then((m) => ({ default: m.SentimentPage })),
)
const TaxPage = lazy(() =>
  import('@tsumi/pages/TaxPage').then((m) => ({ default: m.TaxPage })),
)
const SimulationsPage = lazy(() =>
  import('@tsumi/pages/SimulationsPage').then((m) => ({ default: m.SimulationsPage })),
)
const ComparatorPage = lazy(() =>
  import('@tsumi/pages/ComparatorPage').then((m) => ({ default: m.ComparatorPage })),
)
const TsumiJeliLabPage = lazy(() =>
  import('@tsumi/pages/JeliLab').then((m) => ({ default: m.JeliLabPage })),
)
const MarketsPage = lazy(() =>
  import('@foroba-app/pages/MarketsPage').then((m) => ({ default: m.MarketsPage })),
)

const PageLoader = () => (
  <div style={{ padding: '40px 32px', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
    Loading…
  </div>
)

const wrap = (el: React.ReactElement) => (
  <Suspense fallback={<PageLoader />}>{el}</Suspense>
)

/** Pazuru context wrapper — provides CurrencyProvider + PrivacyProvider for all Pazuru pages */
function PazuruLayout() {
  return (
    <CurrencyProvider>
      <PrivacyProvider>
        <Outlet />
      </PrivacyProvider>
    </CurrencyProvider>
  )
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // Public
      { path: '/login', element: <LoginPage /> },

      // Home dashboard
      { path: '/', element: <DashboardPage /> },

      // /foroba → redirect to Profile (replaces the old stub, IOS-6)
      { path: '/foroba', element: <Navigate to="/foroba/profile" replace /> },

      // Foroba mobile-first account pages (IOS-6.7–6.10)
      { path: '/foroba/profile',    element: <ProfilePage /> },
      { path: '/foroba/settings',   element: <SettingsPage /> },
      { path: '/foroba/security',   element: <SecurityPage /> },
      { path: '/foroba/household',  element: <HouseholdPage /> },

      // Import statement page — CSV/PDF via native file picker or web <input>
      { path: '/import', element: <ImportPage /> },

      // Legal / About page (IOS-6.11)
      { path: '/legal', element: <LegalPage /> },

      // ── Pazuru section — bank accounts, transactions, analytics ──
      // Wrapped with CurrencyProvider + PrivacyProvider (required by <Money>).
      // Mounted at original paths so internal navigate() and <Link> calls work.
      // Omitted in the Yiriba-only flavour (BR-14).
      ...(FLAVOR.hasNaforo ? [{
        element: <PazuruLayout />,
        children: [
          { path: '/accounts',            element: wrap(<AccountsPage />) },
          { path: '/accounts/:accountId', element: wrap(<AccountDetailPage />) },
          { path: '/transactions',        element: wrap(<TransactionsPage />) },
          { path: '/recurring',           element: wrap(<RecurringPage />) },
          { path: '/spending',            element: wrap(<SpendingPage />) },
          { path: '/forecasts',           element: wrap(<ForecastsPage />) },
          { path: '/budgets',             element: wrap(<BudgetsPage />) },
          { path: '/goals',              element: wrap(<GoalsPage />) },
          { path: '/debts',              element: wrap(<DebtsPage />) },
          { path: '/pazuru/lab',        element: wrap(<PazuruJeliLabPage />) },
          { path: '/pazuru/import-log', element: wrap(<PazuruImportLogPage />) },
        ],
      }] : []),

      // ── Tsumi section — portfolio, ETF, crypto, market analysis ──
      // Mounted at original paths so internal tsumi navigation links work.
      // Omitted in the Naforo-only flavour (BR-14).
      ...(FLAVOR.hasYiriba ? [
        { path: '/portfolio',             element: wrap(<PortfolioPage />) },
        { path: '/etf',                   element: wrap(<EtfExplorerPage />) },
        { path: '/crypto',                element: wrap(<CryptoExplorerPage />) },
        { path: '/asset/:type/:id',       element: wrap(<AssetDetailPage />) },
        { path: '/analysis',              element: wrap(<MarketAnalysisPage />) },
        { path: '/backtest',              element: wrap(<BacktestPage />) },
        { path: '/screener',              element: wrap(<ScreenerPage />) },
        { path: '/sentiment',             element: wrap(<SentimentPage />) },
        { path: '/tax',                   element: wrap(<TaxPage />) },
        { path: '/simulate',              element: wrap(<SimulationsPage />) },
        { path: '/compare',               element: wrap(<ComparatorPage />) },
        { path: '/tsumi/lab',             element: wrap(<TsumiJeliLabPage />) },
        { path: '/markets',               element: wrap(<MarketsPage />) },
      ] : []),

      // Anything else (including the other app's paths in a single-app flavour) → Home
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])
